import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Filter, Users, UserCheck, UserX, Clock } from 'lucide-react';
import { Button } from '../../components/common/Button/Button';
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '../../components/common/Table/Table';
import { Badge } from '../../components/common/Badge/Badge';
import { LoadingSpinner } from '../../components/common/LoadingSpinner/LoadingSpinner';
import { Input } from '../../components/common/Input/Input';
import { UserModal } from '../../components/users/UserModal';
import { User, UserFilter, UserStatus, UserRole } from '../../types';
import { apiService } from '../../services/api.service';

export const UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<UserFilter>({
    page: 1,
    limit: 10,
  });
  const [totalUsers, setTotalUsers] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
  });

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [filters]);

  useEffect(() => {
    // Debounce search
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }));
      } else {
        setFilters(prev => ({ ...prev, search: undefined, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers(filters);
      setUsers(response.data);
      setTotalUsers(response.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Fallback to empty state on error
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const statsResponse = await apiService.getUserStats();
      setStats({
        total: statsResponse.totalUsers,
        active: statsResponse.activeUsers,
        inactive: statsResponse.inactiveUsers,
        pending: statsResponse.pendingUsers,
      });
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiService.deleteUser(id.toString());
        fetchUsers();
        fetchUserStats();
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const getRoleBadge = (role: UserRole) => {
    const roleConfig: Record<UserRole, { variant: 'danger' | 'warning' | 'default' | 'success'; label: string }> = {
      [UserRole.SYSTEM_ADMIN]: { variant: 'danger', label: 'System Admin' },
      [UserRole.ENGINEER_IN_CHIEF]: { variant: 'danger', label: 'Engineer-in-Chief' },
      [UserRole.EXECUTIVE_ENGINEER]: { variant: 'warning', label: 'Executive Engineer' },
      [UserRole.ASSISTANT_ENGINEER]: { variant: 'default', label: 'Assistant Engineer' },
      [UserRole.EDITOR]: { variant: 'warning', label: 'Editor' },
      [UserRole.VIEWER]: { variant: 'default', label: 'Viewer' },
    };

    const config = roleConfig[role] || { variant: 'default', label: role };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: UserStatus) => {
    const statusConfig: Record<UserStatus, { variant: 'success' | 'default' | 'danger' | 'warning'; label: string }> = {
      [UserStatus.ACTIVE]: { variant: 'success', label: 'Active' },
      [UserStatus.INACTIVE]: { variant: 'default', label: 'Inactive' },
      [UserStatus.SUSPENDED]: { variant: 'danger', label: 'Suspended' },
      [UserStatus.PENDING_APPROVAL]: { variant: 'warning', label: 'Pending' },
      [UserStatus.RETIRED]: { variant: 'default', label: 'Retired' },
      [UserStatus.TRANSFERRED]: { variant: 'default', label: 'Transferred' },
    };

    const config = statusConfig[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleStatusFilter = (status?: UserStatus) => {
    setFilters(prev => ({ ...prev, status, page: 1 }));
  };

  const handleRoleFilter = (role?: UserRole) => {
    setFilters(prev => ({ ...prev, role, page: 1 }));
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage system users and their permissions
          </p>
        </div>
        <Button 
          onClick={handleCreate}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <UserX className="w-5 h-5 text-gray-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.inactive}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search users by name, email, or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filters.status || ''}
              onChange={(e) => handleStatusFilter(e.target.value as UserStatus || undefined)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value={UserStatus.ACTIVE}>Active</option>
              <option value={UserStatus.INACTIVE}>Inactive</option>
              <option value={UserStatus.PENDING_APPROVAL}>Pending</option>
              <option value={UserStatus.SUSPENDED}>Suspended</option>
            </select>
            
            <select
              value={filters.role || ''}
              onChange={(e) => handleRoleFilter(e.target.value as UserRole || undefined)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Roles</option>
              <option value={UserRole.SYSTEM_ADMIN}>System Admin</option>
              <option value={UserRole.ENGINEER_IN_CHIEF}>Engineer-in-Chief</option>
              <option value={UserRole.EXECUTIVE_ENGINEER}>Executive Engineer</option>
              <option value={UserRole.ASSISTANT_ENGINEER}>Assistant Engineer</option>
              <option value={UserRole.EDITOR}>Editor</option>
              <option value={UserRole.VIEWER}>Viewer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>User</TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Department</TableHeaderCell>
              <TableHeaderCell>Employee ID</TableHeaderCell>
              <TableHeaderCell>Last Login</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="text-gray-500">
                    {loading ? (
                      <LoadingSpinner size="sm" text="Loading..." />
                    ) : (
                      <div>
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p>No users found</p>
                        {searchTerm && (
                          <p className="text-sm mt-1">
                            Try adjusting your search or filters
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white text-sm font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-gray-500 text-sm">{user.email}</div>
                        {user.designation && (
                          <div className="text-gray-400 text-xs">{user.designation}</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell>
                    <div>
                      <div className="text-gray-900">{user.department || 'Not assigned'}</div>
                      {user.division && (
                        <div className="text-gray-500 text-sm">{user.division}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">
                      {user.employee_id || 'Not assigned'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="text-gray-900">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'
                      }
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        leftIcon={<Edit className="w-4 h-4" />}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(user.id)}
                        leftIcon={<Trash2 className="w-4 h-4" />}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalUsers > (filters.limit || 10) && (
          <div className="px-6 py-3 border-t bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((filters.page || 1) - 1) * (filters.limit || 10) + 1} to{' '}
                {Math.min((filters.page || 1) * (filters.limit || 10), totalUsers)} of{' '}
                {totalUsers} users
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={filters.page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={(filters.page || 1) * (filters.limit || 10) >= totalUsers}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      <UserModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        user={editingUser}
        onSuccess={() => {
          setShowModal(false);
          fetchUsers();
          fetchUserStats();
        }}
      />
    </div>
  );
};